import classNames from 'classnames';
import React, { ForwardedRef, ReactNode } from 'react';

import { Card } from '../Card/index.js';
import { HStack } from '../../Layout/index.js';
import Typography from '../../Typography/index.js';

export interface DataCardRow {
  /**
   * The data point label.
   * @example "Plan Name"
   */
  rowHeader: string | ReactNode;

  /**
   * The value of the data point.
   * @example "Kaiser Medicare"
   */
  rowData: string | ReactNode;
}

export interface DataCardProps {
  /**
   * The content to display on the left side of the card header which
   * should usually be a company logo or a typography component.
   */
  cardHeaderLeft: ReactNode;

  /**
   * The content to display on the right side of the card header which
   * should usually be a button or a typography component.
   */
  cardHeaderRight?: ReactNode;

  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * An array of data points to display in the card.
   */
  rows: DataCardRow[];
}

/**
 * When we process a patient uploaded card, we take the image that the patient
 * uploaded, run OCR (Optical character recognition) on the card to pull the
 * relevant fields, and then process those fields using
 * RTE (Real-time eligibility).
 *
 * Once this process is completed, we provide a digital representation of that
 * card for the patient. Different payers have different fields, so this
 * component is meant to be flexible in the # of lines it can present, and also
 * show the payers logo dynamically. If no logo is present, we use type to
 * represent the payer. The two variants here are used for different formats in
 * demos.
 *
 * These cards always span to the full available width of the container.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1812%3A1330
 */
const DataCard = React.forwardRef(
  (
    { cardHeaderLeft, cardHeaderRight, className, rows }: DataCardProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <Card
        ref={ref}
        className={classNames('data-card', className)}
        size="medium"
      >
        <Card.Header background="gray">
          <HStack width="100%" justifyContent="space-between">
            {cardHeaderLeft}
            {cardHeaderRight}
          </HStack>
        </Card.Header>
        {rows.length > 0 && (
          <Card.Body>
            {rows.map(({ rowHeader, rowData }, i) => {
              const isLastRow = i === rows.length - 1;
              return (
                <React.Fragment key={i}>
                  <div className={'content-row'}>
                    {typeof rowHeader === 'string' ? (
                      <Typography variant="title-medium">
                        {rowHeader}
                      </Typography>
                    ) : (
                      rowHeader
                    )}
                    {typeof rowData === 'string' ? (
                      <Typography variant="title-medium">{rowData}</Typography>
                    ) : (
                      rowData
                    )}
                  </div>
                  {!isLastRow && <div className={'divider'} role="separator" />}
                </React.Fragment>
              );
            })}
          </Card.Body>
        )}
      </Card>
    );
  },
);

DataCard.displayName = 'DataCard';

export default DataCard;
